import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface GroupSettingsProps {
  groupId: string;
  isAdmin: boolean;  // Added this prop to the interface
}

export const GroupSettings = ({ groupId, isAdmin }: GroupSettingsProps) => {
  const { data: settings, isLoading } = useQuery({
    queryKey: ['group-settings', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_settings')
        .select('*')
        .eq('group_id', groupId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (!isAdmin) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Only group administrators can view settings
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No settings found for this group
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Allow Member Posts</Label>
          <p className="text-sm text-muted-foreground">
            Let members create posts in this group
          </p>
        </div>
        <Switch checked={settings.allow_member_posts} />
      </div>
      
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Require Post Approval</Label>
          <p className="text-sm text-muted-foreground">
            Review posts before they appear in the group
          </p>
        </div>
        <Switch checked={settings.require_post_approval} />
      </div>
      
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>Member Invites</Label>
          <p className="text-sm text-muted-foreground">
            Allow members to invite others to the group
          </p>
        </div>
        <Switch checked={settings.member_invite_allowed} />
      </div>
    </div>
  );
};